import { AgentSchema } from "@/v2/types/client";
import { webConfig } from "@/v2/utils/config";

const LOCAL_PREVIEW_AGENT_COOKIE = "pitaliy_local_preview_agent";

export type LocalPreviewAgentSettings = Partial<
  Pick<
    AgentSchema,
    | "agent_name"
    | "description"
    | "theme_color"
    | "font_family"
    | "button_theme"
    | "image_url"
    | "display_mode"
    | "header_display_option"
    | "background_pc"
    | "background_sp"
    | "background_style_type"
    | "background_color"
    | "background_gradient"
    | "desktop_view_option"
    | "character_proportion"
    | "character_position"
    | "character_size"
    | "character_position_sp"
    | "character_size_sp"
    | "talking_character_position"
    | "talking_character_size"
    | "talking_character_position_sp"
    | "talking_character_size_sp"
    | "random_character_enabled"
    | "character_hint_enabled"
    | "character_hint_mode"
    | "character_hint_interval_seconds"
    | "character_hint_messages"
    | "floating_feature_text_color"
    | "design_option"
    | "display_position_option"
    | "quick_reply_messages"
    | "first_fixed_message"
    | "start_consent_required"
    | "start_consent_label"
    | "start_terms_text"
    | "start_privacy_text"
    | "faq_flag"
    | "default_special_feature"
    | "gif_datas"
  >
> & { agent_id?: string };

export const loadLocalPreviewAgentSettings = (
  agentId?: string,
): LocalPreviewAgentSettings => {
  if (typeof document === "undefined") return {};
  if (!agentId) return {};

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${LOCAL_PREVIEW_AGENT_COOKIE}=`));

  if (!cookie) return {};

  try {
    const parsed = JSON.parse(
      decodeURIComponent(cookie.split("=").slice(1).join("=")),
    ) as LocalPreviewAgentSettings;
    if (agentId && parsed.agent_id && parsed.agent_id !== agentId) return {};
    return parsed;
  } catch {
    return {};
  }
};

export const loadRemoteLocalPreviewAgentSettings = async (
  agentId: string,
): Promise<LocalPreviewAgentSettings> => {
  if (!import.meta.env.DEV || !webConfig.VITE_BACKEND_API_URL) return {};

  try {
    const response = await fetch(
      `${webConfig.VITE_BACKEND_API_URL}/web-local-preview-agent-settings/${agentId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      },
    );
    if (!response.ok) return {};
    const data = (await response.json()) as {
      settings?: LocalPreviewAgentSettings;
    };
    return data.settings ?? {};
  } catch {
    return {};
  }
};

const LOCAL_PREVIEW_SCENARIO_COOKIE = "pitaliy_local_preview_scenario";

export const loadLocalPreviewScenario = <T>() => {
  if (typeof document === "undefined") return undefined;

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${LOCAL_PREVIEW_SCENARIO_COOKIE}=`));

  if (!cookie) return undefined;

  try {
    return JSON.parse(
      decodeURIComponent(cookie.split("=").slice(1).join("=")),
    ) as T;
  } catch {
    return undefined;
  }
};
